"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { GolfIcon } from "@/components/icons/golf-icon";
import { routes } from "@/routes";
import { RedirectNotification } from "@/components/common/RedirectNotification";
import { useReturningUserCheck } from "@/hooks/use-returning-user-check";

// Import Section Components
import { HeroSection } from "@/components/landing/sections/hero-section";
import { HowItWorksSection } from "@/components/landing/sections/how-it-works-section";
import { FeaturesDeepDiveSection } from "@/components/landing/sections/features-deep-dive-section";
import { LeaguesSection } from "@/components/landing/sections/leagues-section";
import { ClubsSection } from "@/components/landing/sections/clubs-section";
import { ClubSwapSection } from "@/components/landing/sections/club-swap-section";
import { PricingSection } from "@/components/landing/sections/pricing-section";
import { WhyLocalLinksSection } from "@/components/landing/sections/why-local-links-section";

export default function HomePage() {
  const { shouldShowRedirect, cancelRedirect } = useReturningUserCheck();

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* Redirect notification for returning users */}
      {shouldShowRedirect && (
        <RedirectNotification
          targetUrl={routes.dashboard.root}
          targetName="your dashboard"
          initialCountdown={9}
          onCancel={cancelRedirect}
        />
      )}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <Container className="flex h-14 md:h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <GolfIcon className="h-6 w-6" />
            <span className="text-xl font-bold">Local Links</span>
          </div>
          <nav className="flex items-center gap-4">
            <Link href="#how-it-works" className="hidden md:block">
              <Button variant="ghost">How It Works</Button>
            </Link>
            <Link href="#features-deep-dive" className="hidden md:block">
              <Button variant="ghost">Features</Button>
            </Link>
            <Link href="#leagues-section" className="hidden md:block">
              <Button variant="ghost">Leagues</Button>
            </Link>
            <Link href="#clubs-section" className="hidden md:block">
              <Button variant="ghost">Clubs</Button>
            </Link>
            <Link href="#pricing-section" className="hidden md:block">
              <Button variant="ghost">Pricing</Button>
            </Link>
            <Link href={routes.login()}>
              <Button variant="ghost">Login</Button>
            </Link>
            <Link href={routes.register}>
              <Button>Sign Up</Button>
            </Link>
          </nav>
        </Container>
      </header>
      <main className="flex-1">
        <HeroSection />
        <HowItWorksSection />
        <FeaturesDeepDiveSection />
        <LeaguesSection />
        <ClubsSection />
        <ClubSwapSection />
        <PricingSection />
        <WhyLocalLinksSection />
      </main>
      <footer className="border-t py-8 md:py-0">
        <Container className="flex flex-col items-center justify-between gap-4 md:h-24 md:flex-row">
          <p className="text-center text-sm leading-loose text-muted-foreground md:text-left w-full md:w-auto">
            © 2026 McClintock Enterprises DBA Local Links. All rights reserved.
          </p>
          <div className="flex items-center gap-4 w-full md:w-auto justify-center">
            <Link
              href={routes.terms}
              className="text-sm text-muted-foreground hover:text-primary underline underline-offset-4"
            >
              Terms
            </Link>
            <Link
              href={routes.privacy}
              className="text-sm text-muted-foreground hover:text-primary underline underline-offset-4"
            >
              Privacy
            </Link>
          </div>
        </Container>
      </footer>
    </div>
  );
}
